/**
 * UI-7a Task 4 — useVerifyAuditChain hook.
 *
 * Wraps POST `<source-prefix>/audit-log/verify-chain` with an explicit
 * `verify(from?, to?)` trigger. The verification re-walks the entire
 * hash-chain and is expensive — we don't want it firing on every mount,
 * so unlike useAuditLog there's no implicit useEffect fetch.
 *
 * Both date bounds are optional. The backend defaults `from → epoch` and
 * `to → now` when omitted, so callers can verify "everything" with a
 * bare `verify()` call. Dates are serialised to ISO strings before being
 * placed in the body — the backend uses `z.coerce.date()` which accepts
 * both ISO strings and Date instances, but the SSR-safe / Date-free wire
 * format is the simpler default.
 *
 * UI-8d Task 4: the hook accepts a `source` prop that switches the
 * endpoint between the tenant chain (`/admin/compliance/audit-log/
 * verify-chain`) and the platform chain (`/platform/audit-log/
 * verify-chain`). The default is `'tenant'` so existing tenant callers
 * stay untouched.
 *
 * Exposes:
 *   - `verify(from?, to?)` — fires the POST, stores the result.
 *   - `isVerifying` — true while the request is in-flight.
 *   - `result` — `{ valid: boolean, brokenAt?: string } | null`.
 *   - `error` — string | null.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api/client.js'

export type VerifyChainSource = 'tenant' | 'platform'

export interface VerifyChainResult {
  valid: boolean
  brokenAt?: string
}

export interface UseVerifyAuditChainResult {
  verify: (from?: Date, to?: Date) => Promise<void>
  isVerifying: boolean
  result: VerifyChainResult | null
  error: string | null
}

function endpointFor(source: VerifyChainSource): string {
  return source === 'platform'
    ? '/platform/audit-log/verify-chain'
    : '/admin/compliance/audit-log/verify-chain'
}

export function useVerifyAuditChain(
  source: VerifyChainSource = 'tenant',
): UseVerifyAuditChainResult {
  const [isVerifying, setIsVerifying] = useState(false)
  const [result, setResult] = useState<VerifyChainResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const verify = useCallback(
    async (from?: Date, to?: Date): Promise<void> => {
      setIsVerifying(true)
      setError(null)
      try {
        const body: { from?: string; to?: string } = {}
        if (from) body.from = from.toISOString()
        if (to) body.to = to.toISOString()
        const resp = await api.post<VerifyChainResult>(
          endpointFor(source),
          body,
        )
        if (mountedRef.current) {
          setResult(resp)
        }
      } catch (e) {
        if (mountedRef.current) {
          setError(e instanceof Error ? e.message : 'Failed to verify audit chain')
          setResult(null)
        }
      } finally {
        if (mountedRef.current) setIsVerifying(false)
      }
    },
    [source],
  )

  return { verify, isVerifying, result, error }
}
